import React from 'react';
import styled, { css } from 'styled-components';
import { Size } from '../../common/size';
import { baseFontCss } from '../../common/mixins';
import { getStringHash } from '../../common/utils';
import { useTestId } from '../../hooks/useTestId';
import { ITheme, IThemePalette, IThemePalette4Gradients } from '../../Themes/interfaces';
import { transparentize } from 'polished';

// TODO: verify this colours with UX
export const avatarColours = (palette: IThemePalette) => {
  const palettes = [palette.primary.second, palette.secondary.first, palette.secondary.second, palette.secondary.third];
  const gradients: (keyof IThemePalette4Gradients)[] = [100, 75, 50];

  const colors = Object.values(palette.alerts).map((alert) => ({
    text: palette.neutral.white,
    border: palette.neutral.white,
    background: alert
  }));

  palettes.forEach((p) => {
    gradients.forEach((base) => {
      colors.push({
        text: palette.neutral.white,
        border: palette.neutral.white,
        background: p[base]
      });
    });
  });

  return {
    avatar: colors
  };
};

export interface IAvatarProps {
  size?: Size;
  img?: string;
  name?: string;
  bordered?: boolean;
  glow?: boolean;
}

const DEFAULT_NAME = 'CMC Markets';

const sizes = {
  [Size.ExtraSmall]: `2rem`,
  [Size.Small]: `2.25rem`,
  [Size.Medium]: `2.5rem`,
  [Size.Large]: `2.75rem`,
  [Size.ExtraLarge]: `3rem`
};

export const getAvatarColor = (str: string, theme: ITheme) => {
  const hash = getStringHash(str);
  const avatarColoursLength = theme.colours.avatar.length;

  return css`
    background: ${theme.colours.avatar[hash % avatarColoursLength].background};
    color: ${theme.colours.avatar[hash % avatarColoursLength].text};
    border-color: ${theme.colours.avatar[hash % avatarColoursLength].border};
  `;
};

const Container = styled.div<Omit<IAvatarProps, 'size'> & { size: Size }>`
  ${baseFontCss};
  display: inline-block;
  position: relative;

  border-radius: ${({ size }) => sizes[size]};
  vertical-align: middle;

  ${({ theme, name = DEFAULT_NAME }) => css`
    ${getAvatarColor(name, theme)}
  `};

  ${({ bordered, size }) =>
    bordered
      ? css`
          height: calc(${sizes[size]} - 0.25rem);
          width: calc(${sizes[size]} - 0.25rem);
          margin: calc(0.25rem - 1px);
          &:after {
            border-style: solid;
            border-width: 1px;
            content: '';
            position: absolute;
            height: ${sizes[size]};
            width: ${sizes[size]};
            top: calc(1px - 0.25rem);
            left: calc(1px - 0.25rem);
            border-radius: ${sizes[size]};
          }
        `
      : css`
          height: ${sizes[size]};
          width: ${sizes[size]};
        `};

  ${({ glow, theme }) =>
    glow &&
    css`
      box-shadow: inset 0 0 0.625rem 1px ${transparentize(0.75, theme.colours.default.background)};
    `};

  ${({ img, size }) => {
    return img
      ? css`
          background-color: transparent;
          background-image: url(${img});
          background-repeat: no-repeat;
          background-size: cover;
          background-position: center;
        `
      : css`
          line-height: ${sizes[size]};
          text-align: center;
          font-size: calc(${sizes[size]} / 2);
        `;
  }};
`;

export const Avatar: React.FC<IAvatarProps> = ({ size = Size.Small, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Avatar');
  const text = rest.name
    ?.match(/\b(\w)/g)
    ?.join('')
    ?.slice(0, 2)
    ?.toUpperCase();

  return (
    <Container size={size} {...generateTestId()} {...rest}>
      {!rest.img && text}
    </Container>
  );
};
